<script setup lang="ts">
import { ref, toRef, watch } from 'vue'
import { NModal, NInput, NButton, useMessage } from 'naive-ui'
import type { LintNote } from '../stores/lint_notes'
import { useLintNotesStore } from '../stores/lint_notes'
import { useRestoreFocusOnClose } from '../composables/useRestoreFocusOnClose'

// Modal that lets the admin steer a regenerated patch with free-form
// guidance ("focus on the timeline; the older claim is the right
// one"). The store enqueues a LintNoteRegenerateJob; the row updates
// in place via the channel after the job lands. This view doesn't
// wait for the regenerate to finish — it just enqueues and closes.

const props = defineProps<{
  show: boolean
  note: LintNote | null
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
}>()

const store = useLintNotesStore()
const message = useMessage()

const hint = ref('')
const submitting = ref(false)
const inputRef = ref<InstanceType<typeof NInput> | null>(null)

// Return focus to the trigger when the modal closes. props.show
// isn't a writable model here, so adapt it to a Ref via toRef.
useRestoreFocusOnClose(toRef(props, 'show'))

// Reset the hint and land focus on the textarea on open. Deferred
// a frame so NModal's focus trap settles first (a plain nextTick loses
// the textarea to the modal's focus sentinel).
watch(
  () => props.show,
  (open) => {
    if (!open) return
    hint.value = ''
    requestAnimationFrame(() => inputRef.value?.focus?.())
  },
)

async function submit() {
  if (!props.note) return
  submitting.value = true
  try {
    await store.regenerate(props.note.id, hint.value.trim())
    message.success('Regenerate queued — the row will refresh when the job lands.')
    emit('update:show', false)
  } catch (e) {
    const reason = e instanceof Error ? e.message : 'Failed to enqueue regenerate'
    message.error(reason)
  } finally {
    submitting.value = false
  }
}

function cancel() {
  emit('update:show', false)
}
</script>

<template>
  <NModal
    :show="show"
    preset="dialog"
    title="Regenerate proposed patch"
    style="width: min(600px, calc(100vw - 32px))"
    :close-on-esc="!submitting"
    :mask-closable="!submitting"
    @update:show="(v: boolean) => emit('update:show', v)"
  >
    <template v-if="note">
      <p class="text-sm text-white/70 m-0 mb-3">
        Refining
        <code class="font-mono text-xs">{{ note.kind }}</code>
        on
        <code class="font-mono text-xs">{{ note.page_path }}</code
        >. Add free-form guidance below — the model will use it to steer the next patch — or leave it blank to just
        regenerate against the current page.
      </p>
      <NInput
        ref="inputRef"
        v-model:value="hint"
        type="textarea"
        :rows="4"
        placeholder="e.g. focus on the timeline; the older claim is the right one"
        :disabled="submitting"
        data-testid="lint-edit-prompt"
      />
    </template>

    <template #action>
      <div class="flex gap-2 justify-end">
        <NButton :disabled="submitting" @click="cancel">Cancel</NButton>
        <NButton type="primary" :loading="submitting" @click="submit" data-testid="lint-edit-submit">
          Regenerate
        </NButton>
      </div>
    </template>
  </NModal>
</template>
